// Home v2 — token detail panel. Shown in the desktop right column (or a mobile
// sheet) when an asset row is selected. Actions route into the real Send /
// Receive / Swap flows (Unshield instead of Swap in Private mode). Uses the already-formatted AssetRowData from the row.
import React from 'react'
import { Pressable, View } from 'react-native'

import shortenAddress from '@ambire-common/utils/shortenAddress'
import OpenIcon from '@common/assets/svg/OpenIcon'
import ReceiveIcon from '@common/assets/svg/ReceiveIcon'
import SendIcon from '@common/assets/svg/SendIcon'
import SwapIcon from '@common/assets/svg/SwapIcon'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import CopyText from '@common/components/CopyText'
import TokenIcon from '@common/components/TokenIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { openInTab } from '@common/utils/links'
import { Mode } from '@web/contexts/appModeContext'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'

import { AssetRowData } from '../useHomeDashboard'
import { getDeltaColor, getModeAccent, RADII } from '../tokens'

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

type Props = {
  asset: AssetRowData
  mode: Mode
  embedded?: boolean
  onHide: () => void
  onSend: () => void
  onReceive: () => void
  onSwap: () => void
  onUnshield: () => void
}

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => {
  const { theme } = useTheme()
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.homePageBackground as string,
        borderWidth: 1,
        borderColor: theme.homeCardBorder as string,
        borderRadius: RADII.md,
        paddingVertical: 10,
        paddingHorizontal: 12
      }}
    >
      <Text
        fontSize={11}
        appearance="secondaryText"
        style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {label}
      </Text>
      <View style={{ marginTop: 4 }}>
        {typeof value === 'string' ? (
          <Text fontSize={14} weight="semiBold">
            {value}
          </Text>
        ) : (
          value
        )}
      </View>
    </View>
  )
}

const ActionButton = ({
  label,
  Icon,
  onPress,
  primary,
  accent,
  flex
}: {
  label: string
  Icon: typeof SendIcon
  onPress: () => void
  primary?: boolean
  accent: string
  flex: number
}) => {
  const { theme } = useTheme()
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: 40,
        paddingHorizontal: 12,
        borderRadius: RADII.pill,
        backgroundColor: primary ? accent : (theme.homeControlBackground as string),
        borderWidth: primary ? 0 : 1,
        borderColor: theme.homeCardBorder as string
      }}
    >
      <Icon width={16} height={16} color={primary ? '#fff' : (theme.iconSecondary as string)} />
      <Text
        fontSize={14}
        weight="semiBold"
        numberOfLines={1}
        color={primary ? '#fff' : (theme.primaryText as string)}
        style={{ flexShrink: 1 }}
      >
        {label}
      </Text>
    </Pressable>
  )
}

const TokenDetailPanel: React.FC<Props> = ({
  asset,
  mode,
  embedded,
  onHide,
  onSend,
  onReceive,
  onSwap,
  onUnshield
}) => {
  const { theme } = useTheme()
  const { networks } = useNetworksControllerState()
  const accent = getModeAccent(theme, mode)
  const isUp = (asset.deltaPct ?? 0) >= 0

  // Hide the contract row for native gas tokens (zero address / no address).
  const isContractToken = !!asset.address && asset.address !== ZERO_ADDRESS
  const explorerUrl = networks.find((n) => n.chainId === asset.chainId)?.explorerUrl

  return (
    <View
      style={{
        gap: 20,
        backgroundColor: embedded ? 'transparent' : (theme.homeCardBackground as string),
        borderRadius: RADII.lg,
        padding: embedded ? 0 : 16
      }}
    >
      {/* head */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <TokenIcon
          address={asset.address}
          chainId={asset.chainId}
          width={48}
          height={48}
          withNetworkIcon
        />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <Text fontSize={18} weight="semiBold">
              {asset.symbol}
            </Text>
            {!!asset.network && (
              <Text fontSize={13} appearance="secondaryText">
                on {asset.network}
              </Text>
            )}
          </View>
          {isContractToken && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <Text fontSize={12} appearance="secondaryText" selectable>
                {shortenAddress(asset.address, 13)}
              </Text>
              {!!explorerUrl && (
                <Pressable
                  onPress={() =>
                    openInTab({ url: `${explorerUrl.replace(/\/$/, '')}/address/${asset.address}` })
                  }
                  hitSlop={6}
                >
                  <OpenIcon width={14} height={14} color={theme.iconSecondary as string} />
                </Pressable>
              )}
              <CopyText
                text={asset.address}
                iconWidth={14}
                iconHeight={14}
                iconColor={theme.iconSecondary as string}
              />
            </View>
          )}
        </View>
        <Pressable
          onPress={onHide}
          style={{
            height: 32,
            paddingHorizontal: 14,
            borderRadius: RADII.pill,
            borderWidth: 1,
            borderColor: accent,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Text fontSize={13} weight="medium" color={accent}>
            Hide token
          </Text>
        </Pressable>
      </View>

      {/* balance */}
      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: theme.homeCardBorder as string,
          paddingTop: 16
        }}
      >
        <Text fontSize={28} weight="number_bold">
          {asset.balanceFormatted} {asset.symbol}
        </Text>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            marginTop: 4,
            flexWrap: 'wrap'
          }}
        >
          <Text fontSize={14} weight="medium">
            {asset.balanceUSDFormatted}
          </Text>
          <Text fontSize={12} appearance="secondaryText">
            1 {asset.symbol} ≈ {asset.priceUSDFormatted}
          </Text>
          {asset.deltaPct != null && (
            <Text fontSize={12} weight="medium" color={getDeltaColor(theme, isUp)}>
              {isUp ? '+' : ''}
              {asset.deltaPct.toFixed(2)}%
            </Text>
          )}
        </View>
      </View>

      {/* actions */}
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <ActionButton
          label={`Send ${asset.symbol}`}
          Icon={SendIcon}
          onPress={onSend}
          primary
          accent={accent}
          flex={1.4}
        />
        <ActionButton
          label="Receive"
          Icon={ReceiveIcon}
          onPress={onReceive}
          accent={accent}
          flex={1}
        />
        {/* Swap only moves PUBLIC balances — in Private mode it would open the public
            Swap screen for a shielded token, so offer Unshield for that token instead. */}
        {mode === 'private' ? (
          <ActionButton
            label="Unshield"
            Icon={GlobeIcon}
            onPress={onUnshield}
            accent={accent}
            flex={1}
          />
        ) : (
          <ActionButton label="Swap" Icon={SwapIcon} onPress={onSwap} accent={accent} flex={1} />
        )}
      </View>

      {/* stats */}
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Stat label="Network" value={asset.network || '—'} />
        <Stat label="Decimals" value={String(asset.decimals)} />
        <Stat
          label="24h"
          value={
            <Text fontSize={14} weight="semiBold" color={getDeltaColor(theme, isUp)}>
              {asset.deltaPct != null ? `${isUp ? '+' : ''}${asset.deltaPct.toFixed(2)}%` : '—'}
            </Text>
          }
        />
      </View>
    </View>
  )
}

export default React.memo(TokenDetailPanel)
